/**
 * ETConfiguratorAB — A/B dispatcher for the two PDP configurators (FAT-299).
 *
 * The theme can render both configurators into the same page: the flat modal
 * (#modal-1, control) and the step wizard (#modal-configurator, variant). This
 * script picks one, removes the other from the DOM, and instantiates the single
 * matching class. Removing the loser matters — PostSelection queries the
 * document globally (".post-selection__checkout-btn" and friends), so two
 * configurators in one DOM would cross-wire.
 *
 * Because the configurator only ever becomes visible after the shopper clicks
 * add-to-cart, there is no first-paint flicker to guard against and the
 * Intelligems script can stay fully async — no anti-flicker snippet needed.
 *
 * How Intelligems (or any other tool) selects the variant, in priority order:
 *
 *   1. ?et_cfg=wizard|control        — QA override, also stored for the session
 *   2. window.ETConfiguratorAB.setVariant('wizard')   — call it any time; if the
 *      page already booted, it reloads once so the correct DOM is built
 *   3. <html data-configurator-variant="wizard">      — set the attribute early
 *   4. sessionStorage                — keeps a shopper on one side across pages
 *   5. control                       — default, and the fallback when the tool
 *      is blocked or slow (see the timeout below)
 *
 * The variant is exposed as `data-configurator-variant` on <html> and as an
 * `et:configurator:variant` event so Clarity/Elevar can tag the session.
 */
(function () {
  const VARIANTS = ["control", "wizard"];
  const STORAGE_KEY = "et_cfg_ab_variant";
  const ATTR = "configuratorVariant";

  const api = {
    variant: null,
    booted: false,
    _config: null,
    _pending: null,
  };

  function normalize(value) {
    if (!value) return null;
    const v = String(value).toLowerCase().trim();
    return VARIANTS.indexOf(v) !== -1 ? v : null;
  }

  /**
   * Intelligems preview mode — the URL carries an `ig-preview` token. In preview
   * a QA user deliberately switches variant from the toolbar, which reloads the
   * page. We must NOT let a cached session value short-circuit that: sessionStorage
   * is skipped entirely so the freshly-selected attribute always wins. Real
   * visitors never have this param, so their cross-page stickiness is unchanged.
   */
  function isPreview() {
    try {
      return new URLSearchParams(window.location.search).has("ig-preview");
    } catch (e) {
      return false;
    }
  }

  function fromSession() {
    if (isPreview()) return null;
    try {
      return normalize(sessionStorage.getItem(STORAGE_KEY));
    } catch (e) {
      return null;
    }
  }

  function remember(variant) {
    if (isPreview()) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, variant);
    } catch (e) {
      /* private mode — the session just re-rolls per page */
    }
  }

  function fromUrl() {
    try {
      return normalize(new URLSearchParams(window.location.search).get("et_cfg"));
    } catch (e) {
      return null;
    }
  }

  function fromAttribute() {
    return normalize(document.documentElement.dataset[ATTR]);
  }

  /** Whatever is decided first wins; null means "nobody has decided yet". */
  function resolve() {
    return fromUrl() || fromAttribute() || fromSession();
  }

  /**
   * Public hook for Intelligems. Calling it before boot decides the variant;
   * calling it after boot reloads the page once, because by then the losing
   * configurator has already been removed from the DOM.
   */
  api.setVariant = function (value) {
    const variant = normalize(value);
    if (!variant) return;

    if (!api.booted) {
      document.documentElement.dataset[ATTR] = variant;
      remember(variant);
      if (api._pending) api._pending(variant);
      return;
    }

    if (variant === api.variant) return;
    remember(variant);
    // In preview a QA user switches variant repeatedly; never lock the reload.
    if (isPreview()) {
      window.location.reload();
      return;
    }
    // Only ever reload once per session, so a misconfigured tool can't loop.
    try {
      if (sessionStorage.getItem(STORAGE_KEY + "_reloaded")) return;
      sessionStorage.setItem(STORAGE_KEY + "_reloaded", "1");
    } catch (e) {
      return;
    }
    window.location.reload();
  };

  function commit(variant) {
    const modals = {
      control: document.getElementById("modal-1"),
      wizard: document.getElementById("modal-configurator"),
    };

    // Fall back to whichever configurator actually exists — outside the test
    // population only one of the two is rendered.
    if (!modals[variant]) {
      variant = modals.wizard ? "wizard" : "control";
    }

    VARIANTS.forEach(function (other) {
      if (other !== variant && modals[other]) modals[other].remove();
    });

    api.variant = variant;
    api.booted = true;
    document.documentElement.dataset[ATTR] = variant;
    remember(variant);

    const cfg = api._config;
    // Both are `class` declarations in classic scripts, so they live in the
    // global lexical scope rather than on `window` — reference them directly.
    let Ctor = null;
    if (variant === "wizard") {
      if (typeof ETConfigurator === "function") Ctor = ETConfigurator;
    } else if (typeof PostSelection === "function") {
      Ctor = PostSelection;
    }

    if (cfg && typeof Ctor === "function") {
      api.instance = new Ctor(cfg.product, cfg.defaultVariant);
    }

    // Tag the session so Clarity recordings can be filtered by bucket, which is
    // how the test gets watched alongside the Intelligems numbers.
    if (typeof window.clarity === "function") {
      window.clarity("set", "configurator_variant", variant);
    }

    document.dispatchEvent(
      new CustomEvent("et:configurator:variant", { detail: { variant: variant } })
    );
  }

  /**
   * Called by post-selection.liquid once the markup is in the page.
   * `timeout` is how long we wait for the testing tool before defaulting to
   * control; 0 means "decide immediately".
   */
  api.boot = function (config) {
    api._config = config;

    const decided = resolve();
    if (decided) {
      commit(decided);
      return;
    }

    if (!config.abActive || !config.timeout) {
      commit("control");
      return;
    }

    let settled = false;
    let observer = null;
    const finish = function (variant) {
      if (settled) return;
      settled = true;
      api._pending = null;
      if (observer) {
        try {
          observer.disconnect();
        } catch (e) {
          /* no-op */
        }
      }
      commit(variant || resolve() || "control");
    };

    api._pending = finish;

    // Primary signal: watch <html> for Intelligems setting the variant
    // attribute. This is timing-independent — it fires the instant the
    // attribute appears, however late Intelligems (esp. in preview) applies it.
    // A fixed timeout raced this and defaulted to control before the wizard was
    // ever selected.
    if (window.MutationObserver) {
      observer = new MutationObserver(function () {
        const v = fromAttribute();
        if (v) finish(v);
      });
      try {
        observer.observe(document.documentElement, {
          attributes: true,
          attributeFilter: ["data-configurator-variant"],
        });
      } catch (e) {
        observer = null;
      }
    }

    // Belt-and-suspenders: Intelligems' own ready signal (immediate if already
    // loaded), plus a tick so a same-frame modification is stamped first.
    onIntelligemsReady(function () {
      window.setTimeout(function () {
        finish(resolve());
      }, 60);
    });

    // If the shopper clicks add-to-cart before the variant is resolved (e.g.
    // Intelligems is blocked), resolve now and open the configurator so the
    // button is never dead.
    document.querySelectorAll(".post-selection__atc-btn").forEach(function (btn) {
      btn.addEventListener(
        "click",
        function (e) {
          if (settled) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          finish(resolve());
          if (api.instance && typeof api.instance._handleATCBtn === "function") {
            api.instance._handleATCBtn();
          }
        },
        true
      );
    });

    // Last-resort fallback for when Intelligems is truly absent. Kept generous
    // so a merely slow Intelligems still wins — nothing here delays rendering,
    // the configurator only opens on click.
    var fallbackMs = Math.max(config.timeout || 0, 15000);
    window.setTimeout(function () {
      finish(resolve());
    }, fallbackMs);
  };

  /**
   * Run cb when Intelligems is ready, immediately if it already is. Hooks both
   * the `ig:ready` event and the `window.onIgReady` callback (which may already
   * be a function or an array) without clobbering an existing handler.
   */
  function onIntelligemsReady(cb) {
    try {
      window.addEventListener("ig:ready", cb, { once: true });
    } catch (e) {
      /* no-op */
    }
    const prev = window.onIgReady;
    if (typeof prev === "function") {
      window.onIgReady = [prev, cb];
    } else if (Array.isArray(prev)) {
      prev.push(cb);
    } else {
      window.onIgReady = cb;
    }
  }

  window.ETConfiguratorAB = api;
})();
